import {
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useState } from 'react';

export type Columns<T> = Column<T>[];

export interface Column<T> {
  header: React.ReactNode;
  content: (rowData: T, rowIndex: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
}

type ExpandableRows = Record<number, boolean>;

export function useExpandableRows() {
  const [expandableRows, setExpandableRows] = useState<ExpandableRows>({});

  const toggleExpandableRow = useCallback(
    (rowId: number) => {
      setExpandableRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    },
    [setExpandableRows],
  );

  const renderExpandableRowToggle = (rowId: number) => {
    return (
      <IconButton onClick={() => toggleExpandableRow(rowId)}>
        {expandableRows[rowId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    );
  };

  return { expandableRows, toggleExpandableRow, renderExpandableRowToggle };
}

export function MoDataTablePagination({
  count,
  page,
  pageSize,
  onPageChange,
}: {
  count: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={page}
      rowsPerPage={pageSize}
      rowsPerPageOptions={[pageSize]}
      onPageChange={(_, newPage) => onPageChange(newPage)}
    />
  );
}

export function MoDataTable<T>({
  columns,
  data,
  rowSx = {},
  sx = {},
  footer,
  expandableRowContent = () => null,
  expandableRows = {},
  getRowSx,
}: {
  columns: Column<T>[];
  data: T[];
  sx?: SxProps<Theme>;
  rowSx?: SxProps<Theme>;
  footer?: React.ReactNode;
  expandableRowContent?: (rowData: T, rowIndex: number) => React.ReactNode;
  expandableRows?: ExpandableRows;
  getRowSx?: (rowData: T, rowIndex: number) => Record<string, unknown>;
}): React.ReactElement {
  return (
    <TableContainer>
      <Table
        sx={[
          // Stopgap: on mobile the table keeps its natural width and the
          // container scrolls horizontally instead of squishing columns.
          // A proper responsive card layout is tracked as a follow-up.
          (theme) => ({
            [theme.breakpoints.down('sm')]: { minWidth: 'max-content' },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <TableHead>
          <TableRow sx={rowSx}>
            {columns.map((col, i) => (
              <TableCell key={i} align={col.align} sx={col.sx}>
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.map((rowData, i) => (
              <React.Fragment key={i}>
                <TableRow sx={getRowSx ? { ...getRowSx(rowData, i) } : rowSx}>
                  {columns.map((col, j) => (
                    <TableCell key={j} align={col.align} sx={col.sx}>
                      {col.content(rowData, i)}
                    </TableCell>
                  ))}
                </TableRow>
                {expandableRows[i] && (
                  <TableRow sx={rowSx}>
                    <TableCell colSpan={columns.length}>
                      {expandableRowContent(rowData, i)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
        </TableBody>
        {footer && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={columns.length}>{footer}</TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
